import { api, queryKeys, transferInput, type TransferInput } from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { PageHeader } from '../../components/PageHeader';
import { formatLds, formatUsd } from '../../lib/format';
import { Alert, Button, Card, FormGroup, Input, Select, StatCard, StatCards } from '../../ui';
import { WALLET_TABS } from './Deposit';

export function Transfer() {
  const queryClient = useQueryClient();

  const wallet = useQuery({
    queryKey: queryKeys.wallet(),
    queryFn: () => api.wallet.balances().then((r) => r.data),
  });

  const form = useForm<TransferInput>({
    resolver: zodResolver(transferInput),
    defaultValues: { to_user_code: '', amount_usd: 0, from_wallet: 'income', transaction_pin: '' },
  });

  const recipientCode = form.watch('to_user_code');

  // Confirm who is being paid before the transfer is committed — a mistyped
  // User ID is not recoverable once the ledger row is written.
  const recipient = useQuery({
    queryKey: ['transfer-lookup', recipientCode],
    queryFn: () => api.wallet.lookupRecipient(recipientCode).then((r) => r.data),
    enabled: recipientCode.length >= 6,
    retry: false,
  });

  const submit = useMutation({
    mutationFn: (values: TransferInput) => api.wallet.transfer(values),
    onSuccess: () => {
      form.reset();
      queryClient.invalidateQueries({ queryKey: queryKeys.wallet() });
    },
  });

  return (
    <>
      <PageHeader title="Transfer" description="Send funds to another Alpha One member." tabs={WALLET_TABS} />

      <StatCards>
        <StatCard label="Activation Wallet" value={formatUsd(wallet.data?.activation_balance)} />
        <StatCard label="Income Wallet" value={formatLds(wallet.data?.income_balance_lds)} />
      </StatCards>

      <Card>
        {submit.isSuccess ? <Alert tone="success">Transfer complete.</Alert> : null}
        {submit.error ? <Alert tone="danger">{(submit.error as Error).message}</Alert> : null}

        <form onSubmit={form.handleSubmit((values) => submit.mutate(values))} noValidate>
          <FormGroup
            label="Recipient User ID"
            error={form.formState.errors.to_user_code?.message}
            hint={recipient.data ? `Sending to ${recipient.data.name}` : undefined}
          >
            <Input placeholder="AO123456" {...form.register('to_user_code')} />
          </FormGroup>

          <FormGroup label="From Wallet" error={form.formState.errors.from_wallet?.message}>
            <Select {...form.register('from_wallet')}>
              <option value="income">Income Wallet</option>
              <option value="activation">Activation Wallet</option>
            </Select>
          </FormGroup>

          <FormGroup label="Amount (USD)" error={form.formState.errors.amount_usd?.message}>
            <Input type="number" step="0.01" {...form.register('amount_usd')} />
          </FormGroup>

          <FormGroup label="Note" error={form.formState.errors.note?.message}>
            <Input {...form.register('note')} />
          </FormGroup>

          <FormGroup label="Transaction PIN" error={form.formState.errors.transaction_pin?.message}>
            <Input type="password" {...form.register('transaction_pin')} />
          </FormGroup>

          <Button type="submit" variant="gold" disabled={submit.isPending || !recipient.data}>
            {submit.isPending ? 'Sending…' : 'Send Transfer'}
          </Button>
        </form>
      </Card>
    </>
  );
}
